import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { environment } from '#environment';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentService } from '@qvs/core/env';
import { selectUserRole, UserService } from '@qvs/core/user';
import { filter, take } from 'rxjs/operators';
import { ready } from '~/modules/app/slice';
import { windowToken } from '~/shared/helpers/window-helper';
import { Store } from '@ngrx/store';
import { ApiConfigurationService } from '~/shared/services/api-configuration.service';
import { PersistenceService } from '~/shared/services/persistence.service';
import { RemoteConfigService } from '@qvs/core/remoteConfig';
import { PortfolioService } from '~/modules/portfolio/portfolio.service';
import { MandateTypeService } from '~/modules/mandateTypes/mandateType.service';
import { NewsService } from '~/modules/news/news.service';
import { AccountService } from '~/modules/accounts/account.service';
import { EDocumentsService } from '~/modules/e-documents/e-documents.service';
import { NoInternetConnectionComponent } from '~/shared/components/no-internet-connection/no-internet-connection.component';
import { IonicModule } from '@ionic/angular';

@Component({
  selector: 'qvs-ebanking-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, NoInternetConnectionComponent, IonicModule],
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit {
  environmentIsReady = false;
  showNoInternetConnectionIndicator = false;

  constructor(
    @Inject(windowToken) private readonly window: Window,
    private readonly store: Store,
    private readonly ngZone: NgZone,
    private readonly titleService: Title,
    private readonly translateService: TranslateService,
    private readonly apiConfigurationService: ApiConfigurationService,
    private readonly environmentService: EnvironmentService,
    private readonly persistenceService: PersistenceService,
    private readonly remoteConfigService: RemoteConfigService,
    private readonly userService: UserService,
    private readonly portfolioService: PortfolioService,
    private readonly mandateTypeService: MandateTypeService,
    private readonly accountService: AccountService,
    private readonly newsService: NewsService,
    private readonly eDocumentsService: EDocumentsService
  ) {
    this.translateService.setFallbackLang('en');
    this.translateService.use('en');

    /** check if user has internet connection **/
    this.window.addEventListener('online', () => this.updateNetworkStatus(true));
    this.window.addEventListener('offline', () => this.updateNetworkStatus(false));
  }

  ngOnInit() {
    this.initApp();
  }

  // TODO 04-Jun-2026/braune: this should be in an effect, not in this component
  async initApp() {
    this.titleService.setTitle(environment.appTitle);
    const env = await this.environmentService.ready();

    this.updateNetworkStatus(this.window.navigator.onLine);

    if (env.hasError) {
      // TODO 28-Mar-2023/braune: handle more gracefully with global exception handling
      alert('Fatal error: Environment failed to load.');
      return;
    } else {
      // TODO 09-Jun-2026/braune: do we really need this? There is no splash screen in this case
      this.environmentIsReady = true;
    }

    await this.persistenceService.init();
    this.remoteConfigService.init();
    await this.apiConfigurationService.init();

    this.store
      .select(selectUserRole)
      .pipe(
        filter((userRole) => !!userRole),
        take(1)
      )
      .subscribe(() => {
        // TODO 06-Jul-2023/braune: implement fetching resources relevant to the home screen first
        this.userService.init();
        this.portfolioService.init();
        this.mandateTypeService.init();
        this.eDocumentsService.init();
        this.newsService.init();
        this.accountService.init();
        return Promise.resolve();
      });

    this.store.dispatch(ready());
  }

  closeNoInternetConnectionIndicator() {
    this.showNoInternetConnectionIndicator = false;
  }

  private updateNetworkStatus(status: boolean) {
    this.ngZone.run(() => {
      this.showNoInternetConnectionIndicator = !status;
    });
  }
}
